import React, { useState, useEffect } from 'react';
import { useScreen } from 'common/hooks/useScreen';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { Box, Typography, Stack } from '@mui/material';
import { userRole } from 'TAP/styles/genericStyles/userRole';
import { NUMBER_ARRAY } from 'common/global/constants';
import { formatTime } from 'TAP/utils/commonMethods';
import { RedTheme } from 'common/global';
import { fontWeightStyles } from 'TAP/styles/genericStyles/fontWeights';
import { MyProfile } from './MyProfile';
import { MyTeamProfile } from './MyTeamProfile';
import { TapGlobalMessages } from 'TAP/global';

const MY_PROFILE_LABEL = 'My Profile';
const MY_TEAM_PROFILE_LABEL = "My Team's Profile";

export const CurriculumAssignmentRequest = () => {
  const [initializer, setInitializer] = useState(false);
  const [logInUserDetails, setLogInUserDetails] = useState('');
  const [selectedTabValue, setSelectedTabValue] = useState(NUMBER_ARRAY.zero);
  const [isManager, setIsManager] = useState(false);
  const [isDisclaimerToShow, setIsDisclaimerToShow] = useState(true);
  const userDetails = localStorage.getItem('userDetails');
  let redtheme = RedTheme;
  const screen = useScreen();

  // Checks if a user is manager based on given role
  const checkIfManager = (role) => {
    return !!(role === userRole.usr3 || role === userRole.usr2);
  };

  // Closes Disclaimer
  const handleCloseDisclaimer = () => {
    setIsDisclaimerToShow(false);
  };

  const containerPadding = {
    mobile: '0px 16px',
    tablet: '0 24px 24px 24px',
    desktop: '0 72px 0 72px',
  };

  const containerStyling = {
    padding: screen.resolveStyles(containerPadding),
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    flexDirection: 'column',
    width: '100%',
  };

  const lastRefreshStyle = {
    fontSize: screen.resolveStyles({
      mobile: '10px',
      tablet: '14px',
      desktop: '14px',
    }),
    lineHeight: '16px',
    pb: '8px',
    color: redtheme.palette.fontColor.gray,
    fontWeight: screen.resolveStyles({
      mobile: fontWeightStyles.fw5.fontWeight,
      tablet: fontWeightStyles.fw6.fontWeight,
      desktop: fontWeightStyles.fw7.fontWeight,
    }),
    fontFamily: '"JohnsonText-Regular"',
  };

  const subHeaderStyle = {
    fontSize: screen.resolveStyles({
      mobile: '10px',
      tablet: '10px',
      desktop: '13px',
    }),
    lineHeight: '16px',
    pb: '8px',
    color: redtheme.palette.fontColor.black,
    fontWeight: screen.resolveStyles({
      mobile: fontWeightStyles.fw5.fontWeight,
      tablet: fontWeightStyles.fw6.fontWeight,
      desktop: fontWeightStyles.fw7_l.fontWeight,
    }),
    fontFamily: '"JohnsonText-Regular"',
  };

  const headingTextStyle = {
    width: '100%',
    fontSize: screen.resolveStyles({
      mobile: '25px',
      tablet: '31px',
      desktop: '39px',
    }),
    lineHeight: screen.resolveStyles({
      mobile: '28px',
      tablet: '36px',
      desktop: '48px',
    }),
    fontFamily: '"JohnsonDisplay-Bold"',
    fontWeight: fontWeightStyles.fw7.fontWeight,
    padding: 0,
    margin: 0,
  };

  const tabsStyle = {
    minHeight: '49px',
    '& .Mui-selected': {
      fontWeight: 'bold',
      color: `${redtheme.palette.fontColor.black_gray} !important`,
    },
  };

  const tabStyle = {
    textTransform: 'none',
    fontSize: '16px',
    fontWeight: fontWeightStyles.fw7.fontWeight,
    lineHeight: '16px',
    fontFamily: '"JohnsonText-Medium"',
    borderBottom: 3,
    borderColor: 'divider',
  };

  const tabIndicatorPropStyle = {
    height: '3px',
    borderRadius: '3px',
    textTransform: 'none',
  };
  const tabsContainerStyle = {
    padding: screen.resolveStyles(containerPadding),
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'column',
    width: '100%',
    marginBottom: '10px'
  };

  const handleTabChange = (_event, newValue) => {
    setSelectedTabValue(newValue);
  };

  useEffect(() => {
    if (!initializer) {
      if (userDetails && Object.keys(userDetails).length) {
        setLogInUserDetails(JSON.parse(userDetails));
        setIsManager(checkIfManager(JSON.parse(userDetails).user_role));
      }
      setInitializer(true);
    }
  }, [initializer, userDetails]);

  const lastRefreshed = () => {
    if (logInUserDetails && Object.keys(logInUserDetails).length) {
      return formatTime(logInUserDetails?.last_refresh_date);
    }
  };

  return (
    <>
      <Box
        className='curriculum-assignment-request'
        sx={containerStyling}
        data-testid='curriculum-assignment-request-header-container'
      >
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: '5px' }}>
          <Stack>
            <Typography gutterBottom sx={headingTextStyle}>
              {`Role Profile`}
            </Typography>
            <Typography
              gutterBottom
              variant='div'
              component='div'
              sx={lastRefreshStyle}
              data-testid='lastRefreshDate'
            >
              Last refresh {lastRefreshed()}
            </Typography>
            <Typography
              gutterBottom
              variant='div'
              component='div'
              sx={subHeaderStyle}
              data-testid='subHeader'
            >
              {TapGlobalMessages.userGroupProfile.subHeaderText}
            </Typography>
          </Stack>
        </Box>
      </Box>
      <Box sx={tabsContainerStyle}>
        {isManager ? (
          <Tabs
            value={selectedTabValue}
            onChange={handleTabChange}
            TabIndicatorProps={{
              sx: tabIndicatorPropStyle,
            }}
            sx={tabsStyle}
          >
            <Tab label={MY_PROFILE_LABEL} data-testid='myProfile' sx={tabStyle}></Tab>
            {(logInUserDetails.auth_roleprofile).toLowerCase() === 'y' &&
              <Tab label={MY_TEAM_PROFILE_LABEL} data-testid='myOrganizationTab' sx={tabStyle}></Tab>
            }
          </Tabs>
        ) : (
          <Tabs
            value={0}
            onChange={handleTabChange}
            TabIndicatorProps={{
              sx: tabIndicatorPropStyle,
            }}
            sx={tabsStyle}
          >
            <Tab label='My Profile' data-testid='myProfile' sx={tabStyle}></Tab>
          </Tabs>
        )}
      </Box>
      <Box>
        {selectedTabValue === 0 ? (
          <MyProfile loggedInUserDetails={logInUserDetails} />
        ) : (
          <MyTeamProfile
            handleCloseDisclaimer={handleCloseDisclaimer}
            isDisclaimerToShow={isDisclaimerToShow}
          />
        )}
      </Box>
    </>
  );
};
